import { atom, useAtomValue } from "jotai";
import { useQuery as useConvexQuery } from "convex/react";
import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { api as cyranoApi } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import { activeAccountScopeAtom } from "@/core/state";

import {
  COLOR_PALETTE,
  DEFAULT_NOTIFICATIONS,
  type Creator,
  type NotificationPrefs,
} from "./mockData";

/**
 * Flag : si true, `CreatorOnboardingWizard` s'ouvre même si tous les
 * creators existants sont `is_complete === true`. Sert au bouton
 * "Ajouter un créateur" du `CreatorsPanel` quand l'agence a déjà des
 * créateurs onboardés et veut en créer un nouveau. Le wizard reset le
 * flag à la création du nouveau creator (step identity ok).
 */
export const requestCreatorWizardAtom = atom<boolean>(false);

/**
 * Slug du créateur in-progress que l'user a explicitement fermé via la croix
 * du wizard. Tant qu'il est set, `useWizardData` retourne `hidden` pour ce
 * slug — le wizard ne re-popup pas tout seul. Cleared par :
 *   - le clic "Reprendre l'onboarding" sur la card pending,
 *   - l'arrivée d'un nouveau créateur in-progress (slug différent),
 *   - un reload (memory-only, pas de persist).
 *
 * Historique : avant 2026-06-17 le wizard était "forcé" en in-progress (pas
 * de bouton Annuler/croix), ce qui combiné au plein écran rendait le cockpit
 * inutilisable tant qu'un créateur n'avait pas fini Telegram. Bug rapporté
 * par le user le 2026-06-17.
 */
export const dismissedOnboardingSlugAtom = atom<string | null>(null);

/**
 * Variante du dismiss pour le mode "no-creator" (0 créateur ou bouton
 * "Ajouter un créateur"). Indépendant de `dismissedOnboardingSlug` qui est
 * slug-based (in-progress). Sans cet atom, la fermeture du wizard quand
 * `creatorsCount === 0` n'avait aucun effet : useWizardData retournait
 * immédiatement en mode "no-creator" et le wizard se rouvrait. Cleared par :
 *   - le clic "Ajouter un créateur" (requestedNew → consumer effect),
 *   - le clic "Reprendre" sur une card pending (requestedOnboardingSlug),
 *   - un reload (memory-only).
 *
 * Bug rapporté par le user 2026-06-17 après la première itération du fix :
 * il avait supprimé le créateur Z pendant l'onboarding → 0 créateur →
 * wizard re-popup "Créer un créateur" → clic Annuler ne faisait rien.
 */
export const dismissedNoCreatorWizardAtom = atom<boolean>(false);

/**
 * Slug d'un créateur in-progress pour lequel l'user demande explicitement la
 * réouverture du wizard (typiquement via le bouton "Reprendre" sur sa card).
 * Consommé par `CreatorOnboardingWizard` : il clear `dismissedOnboardingSlug`,
 * pin le slug, puis remet cet atom à `null`.
 */
export const requestedOnboardingSlugAtom = atom<string | null>(null);

/**
 * DOM ref du conteneur `CreatorsPanel` (set au mount, cleared au unmount).
 * Permet a `CreatorOnboardingWizard` de portail-render son chrome solide
 * directement DANS le panneau Creators au lieu de couvrir tout le shell.
 *
 * Quand le panneau Creators n'est pas dans le workspace courant (ref null),
 * le wizard auto-ouvre le panneau Créateurs avant de se rendre, pour ne
 * jamais retomber sur le plein écran shell (cf user feedback 2026-06-17).
 */
export const wizardPanelMountAtom = atom<HTMLElement | null>(null);

/**
 * Source de verite des creators rattaches au compte courant.
 *
 * Phase 3 du plan Convex/Neon split : on lit depuis le control plane Convex
 * (`creators` table). Les fields data plane (revenue_cents, fans_count,
 * status, bot_token, vault_chat_id, etc.) restent en mock pour l'instant —
 * a brancher Phase 4 via FastAPI `/api/creators/{id}/stats` + agentBindings.
 *
 * Convex schema:
 *   { _id, accountId, slug, displayName, avatarUrl?, socialLinks?, createdAt, updatedAt, archivedAt? }
 *
 * Le shape `Creator` UI est un superset : on projete depuis Convex et on
 * defaulte les fields manquants. La couleur est derivee du slug pour eviter
 * d'avoir un champ stocke (les creators ne sont pas censes la choisir
 * eux-memes — c'est de la decoration).
 */

// Couleur déterministe dérivée du slug — fallback quand aucune couleur
// n'est explicitement set côté Convex. Exporté pour partager le même
// algorithme entre Creators panel et Conversations panel (sinon le même
// creator aurait 2 couleurs différentes selon le panel qui l'affiche).
export function colorFromSlug(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

/**
 * La NATURE d'une ligne `creators` : une créatrice, ou un SITE — ou `tous`
 * (20-09-2026), les deux fusionnées, pour l'espace « Tout » de l'app Twitter.
 *
 * Un site (cumgram.com et les suivants) porte des comptes X, des proxys et ses
 * propres clés d'achat — tout est keyé `creator_id` — mais il n'a ni Telegram,
 * ni persona, ni onboarding. Les deux parcs ne se croisent dans aucun écran.
 * La valeur voyage telle quelle jusqu'au backend (`?perimetre=`).
 */
export type NatureDuPerimetre = "creator" | "site" | "tous";

type ConvexCreator = {
  _id: string;
  accountId: string;
  slug: string;
  displayName: string;
  avatarUrl?: string;
  color?: string;
  socialLinks?: {
    telegram_handle?: string;
    description?: string;
    /** Legacy : color vivait dans socialLinks avant Phase 0 (2026-05-08).
     * On lit en fallback pour les rows pas encore backfillees. */
    color?: string;
  };
  createdAt: number;
  archivedAt?: number;
  /** `creator` (défaut, absent en base) ou `site` — cf `creators.kind`. */
  kind?: string | null;
};

type ConnectionData = {
  bot_token_masked: string | null;
  bot_username: string | null;
  bot_first_name: string | null;
  vault_chat_id: number | null;
  /** Pause/active state du bot Telegram. `null` si pas encore d'integration. */
  bot_is_enabled: boolean | null;
  /** Pause globale Redis (`bot_paused_global:{creator_id}`). Indépendante de
   *  `bot_is_enabled` — peut être set par script/admin sans toucher la DB.
   *  Le webhook bypass si l'un OU l'autre est actif, donc la card doit
   *  refléter le OR. */
  bot_is_globally_paused: boolean;
  /** Mode d'activation Cyrano (auto = ON partout stoppable | manual = OFF par
   *  défaut, GO par conv). Le mode « off » a été retiré (2026-07-08) — on coupe
   *  Cyrano via la pause du bot. `null` si pas encore d'intégration. */
  cyrano_mode: "auto" | "manual" | null;
  /** Mode de supervision de Cyrano, source de vérité `creators.supervision_mode`.
   *  `null` = pas de réglage explicite en DB -> repli sur la résolution env côté
   *  backend. */
  supervision_mode: "strict" | "supervise" | "auto" | "manual" | null;

  /* ⚠️ `cyrano_mode` et `supervision_mode` ne sont plus réglés séparément : le
   * sélecteur d'activation (Créatrices → Agent, 3 modes) les pose ENSEMBLE via
   * `POST /api/creators/{id}/cyrano-activation`. Ils restent exposés ici parce
   * que l'écran DÉRIVE de leur combinaison avec `bot_is_enabled` /
   * `bot_is_globally_paused` — cf `deriveActivation` dans `AgentTab.tsx`, qui
   * explique pourquoi « auto » exige les DEUX. Ne pas rebrancher un écran sur
   * l'une des deux colonnes seule. */
  notification_chat_id: number | null;
  notifications: NotificationPrefs | null;
  /** Santé RÉELLE de la connexion Business (source de vérité business_connections,
   *  cf PLAN_CYRANO_BCID_ROBUSTNESS.md). Distincte de la pause utilisateur :
   *  - `connected`        bot lié + connexion active.
   *  - `degraded`         connexion désactivée (disconnect/réconciliateur) ou can_reply=false.
   *  - `expired`          token présent mais aucune connexion captée (Premium expiré / jamais capté).
   *  - `never-connected`  pas d'intégration.
   *  `null` si le backend ne le renvoie pas (rétro-compat). */
  connection_health:
    | "connected"
    | "degraded"
    | "expired"
    | "never-connected"
    | null;
};

type CreatorStats = {
  revenue_cents: number;
  sales_count: number;
  messages_count: number;
  fans_count: number;
};

function projectToUiCreator(
  c: ConvexCreator,
  stats?: CreatorStats,
  conn?: ConnectionData,
): Creator {
  // `status` reflete l'etat runtime EFFECTIF du bot Telegram :
  //   - archive Convex -> paused (priorite haute, l'utilisateur a delete le creator)
  //   - integration DB en pause OU pause Redis globale -> paused
  //     (les deux signaux sont independants ; le webhook bypass si l'un OU
  //     l'autre est actif, donc l'UI affiche pause des qu'au moins un l'est)
  //   - pas encore d'integration configuree (`bot_is_enabled` null/undefined)
  //     -> pending (onboarding incomplet, bouton play/pause non actionnable)
  //   - sinon -> active
  const botPausedDb = conn?.bot_is_enabled === false;
  const botPausedRedis = conn?.bot_is_globally_paused === true;
  const noIntegration = conn?.bot_is_enabled === null || conn?.bot_is_enabled === undefined;
  const status: "active" | "paused" | "pending" = c.archivedAt || botPausedDb || botPausedRedis
    ? "paused"
    : noIntegration
      ? "pending"
      : "active";
  return {
    id: c._id,
    slug: c.slug,
    name: c.displayName || c.slug,
    color: c.color ?? c.socialLinks?.color ?? colorFromSlug(c.slug),
    telegram_handle: c.socialLinks?.telegram_handle,
    description: c.socialLinks?.description,
    status,
    avatar_url: c.avatarUrl,
    bot_token: conn?.bot_token_masked ?? undefined,
    bot_username: conn?.bot_username ?? undefined,
    bot_first_name: conn?.bot_first_name ?? undefined,
    bot_is_enabled: conn?.bot_is_enabled ?? null,
    connection_health: conn?.connection_health ?? null,
    vault_chat_id: conn?.vault_chat_id ?? undefined,
    notification_chat_id: conn?.notification_chat_id ?? undefined,
    notifications: conn?.notifications ?? { ...DEFAULT_NOTIFICATIONS },
    revenue_cents: stats?.revenue_cents ?? 0,
    sales_count: stats?.sales_count ?? 0,
    messages_count: stats?.messages_count ?? 0,
    fans_count: stats?.fans_count ?? 0,
    added_at: c.createdAt,
    kind: c.kind === "site" ? "site" : "creator",
  };
}

type StatsResponse = {
  stats: ({ slug: string } & CreatorStats)[];
  period_days: number;
};

type ConnectionsResponse = {
  connections: ({ slug: string } & ConnectionData)[];
};

/**
 * Stats agregees (revenue lifetime + fans actifs 30j) depuis ClickHouse.
 * Cle de groupement = slug (partage entre Convex et Neon Cyrano).
 *
 * Refetch automatiquement toutes les 5 min (analytics ne change pas en
 * temps reel cote agent IA — pas besoin de live tail).
 *
 * 🪤 Ce "refetch toutes les 5 min" etait ECRIT ici mais pas IMPLEMENTE :
 * `staleTime` dit seulement a partir de quand une donnee est perimee, il ne
 * declenche aucun appel. Et rien d'autre ne pouvait en declencher un —
 * `refetchOnMount: false` (defaut global, cf `QueryProvider`),
 * `refetchOnWindowFocus: false` ici, et le `retry` global ne retente jamais un
 * 4xx. Un SEUL 401 transitoire (fenetre de refresh de la session WorkOS)
 * figeait donc le header sur 0 message / 0 vente / $0.00 jusqu'au prochain
 * hard reload. Vecu le 03-08 sur Trista : 101 455 messages, 114 ventes et
 * 6 012 € en base, affiches a zero. `useCreatorConnectionsBySlug` ci-dessous
 * n'a jamais eu ce probleme — elle poll, donc elle se repare toute seule.
 */
export function useCreatorStatsBySlug(): Record<string, CreatorStats> {
  const { user } = useSession();
  const isAuthenticated = user !== null;
  const { data } = useReactQuery({
    queryKey: ["creators", "stats"],
    queryFn: async (): Promise<StatsResponse> => {
      // Cookie httpOnly first-party fait l'auth (credentials: include)
      return cyranoApi.get<StatsResponse>("/api/creators/stats");
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    // Le filet manquant : un echec ne survit pas plus d'un cycle.
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return useMemo(() => {
    const out: Record<string, CreatorStats> = {};
    // Defensive : si l'API n'est pas encore deployee ou si l'ingress retourne
    // du HTML d'erreur, `data` peut etre une string ou une shape inattendue.
    // sales_count / messages_count peuvent manquer si le backend n'est pas
    // encore deploye avec l'extension (default 0 — affiche "0" pas un crash).
    if (!data || !Array.isArray((data as StatsResponse).stats)) return out;
    for (const s of (data as StatsResponse).stats) {
      out[s.slug] = {
        revenue_cents: s.revenue_cents ?? 0,
        sales_count: s.sales_count ?? 0,
        messages_count: s.messages_count ?? 0,
        fans_count: s.fans_count ?? 0,
      };
    }
    return out;
  }, [data]);
}

/**
 * Etat de connexion (bot Telegram + vault + notif channel) par slug.
 *
 * Lit le data plane Neon via `/api/creators/connections` — repond avec ce
 * qui est stocke en DB sans appels live Telegram (pas de noms de groupes ;
 * juste les chat_ids et le bot username/first_name). Cookie + JWT WorkOS
 * font l'auth.
 *
 * Refetch toutes les 60s : un onboarding qui termine cote backend (webhook
 * Telegram resout vault_chat_id, ou /admin/notification-config qui set le
 * channel) doit etre visible dans le panel sans reload manuel.
 */
export function useCreatorConnectionsBySlug(): Record<string, ConnectionData> {
  const { user } = useSession();
  const isAuthenticated = user !== null;
  const { data } = useReactQuery({
    queryKey: ["creators", "connections"],
    queryFn: async (): Promise<ConnectionsResponse> => {
      return cyranoApi.get<ConnectionsResponse>("/api/creators/connections");
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return useMemo(() => {
    const out: Record<string, ConnectionData> = {};
    if (!data || !Array.isArray((data as ConnectionsResponse).connections)) return out;
    for (const c of (data as ConnectionsResponse).connections) {
      out[c.slug] = {
        bot_token_masked: c.bot_token_masked,
        bot_username: c.bot_username,
        bot_first_name: c.bot_first_name,
        vault_chat_id: c.vault_chat_id,
        bot_is_enabled: c.bot_is_enabled ?? null,
        bot_is_globally_paused: c.bot_is_globally_paused === true,
        cyrano_mode: c.cyrano_mode ?? null,
        supervision_mode: c.supervision_mode ?? null,
        notification_chat_id: c.notification_chat_id,
        notifications: c.notifications,
        connection_health: c.connection_health ?? null,
      };
    }
    return out;
  }, [data]);
}

/**
 * Lecture live des creators selon le scope cross-account courant.
 *
 * Branchement :
 *   - superadmin Dozka                     → listAllForSuperadmin (toutes orgs)
 *   - scope "all" (multi-org normal)       → listForMyAccounts (orgs membres)
 *   - scope "single" / défaut (legacy)     → listForAccount(accountId)
 *
 * Pour les users qui n'ont qu'une seule org, scope=null garde le comportement
 * legacy (lit accounts[0]). Le selecteur UI ne propose "All" que si l'user
 * est membre de ≥2 orgs.
 *
 * `nature` (27-08-2026) choisit le PARC : les créatrices (défaut, comportement
 * historique) ou les périmètres de SITE (cumgram.com et les suivants). Les
 * queries Convex excluent les sites d'office ; `includeSites` les ramène, et
 * on garde alors les sites SEULS — les deux parcs ne se mélangent jamais dans
 * un écran. Cf `app/api/deps/perimetre.py` pour le pendant backend.
 */
export function useCreatorsList(nature: NatureDuPerimetre = "creator"): Creator[] {
  const sitesSeuls = nature === "site";
  // `tous` (20-09-2026) : l'app Twitter de Kayla porte un sélecteur d'ESPACE
  // en tête, et « Tout » y fusionne ses créatrices et les sites qu'elle voit.
  // Les queries Convex excluent les sites d'office ; on les ramène, et on ne
  // retire personne.
  const lesDeux = nature === "tous";
  const me = useConvexQuery(api.users.me);
  const accounts = useConvexQuery(api.accounts.listMine);
  const scope = useAtomValue(activeAccountScopeAtom);
  const isSuperadmin = me?.isSuperadmin === true;

  // Résolution du scope effectif : si pas de choix explicite (atom null), on
  // garde le pattern legacy "1er account". Si choix "single", on respecte.
  // Si l'accountId stocké dans l'atom n'est plus un account valide (l'user a
  // quitté l'org), on retombe sur le 1er account.
  const validAccountIds = useMemo(
    () => new Set((accounts ?? []).map((a) => a._id as string)),
    [accounts],
  );
  const effectiveSingleAccountId = useMemo(() => {
    if (scope?.kind === "single" && validAccountIds.has(scope.accountId)) {
      return scope.accountId as Id<"accounts">;
    }
    return (accounts?.[0]?._id ?? null) as Id<"accounts"> | null;
  }, [scope, validAccountIds, accounts]);
  const isAllScope = scope?.kind === "all";

  // Si superadmin → query cross-account globale.
  const adminCreators = useConvexQuery(
    api.creators.listAllForSuperadmin,
    isSuperadmin ? { includeSites: sitesSeuls || lesDeux } : "skip",
  );
  // Scope "all" non-superadmin → union des accounts membres.
  const myAccountsCreators = useConvexQuery(
    api.creators.listForMyAccounts,
    !isSuperadmin && isAllScope ? { includeSites: sitesSeuls || lesDeux } : "skip",
  );
  // Scope "single" / défaut → un account.
  const accountCreators = useConvexQuery(
    api.creators.listForAccount,
    !isSuperadmin && !isAllScope && effectiveSingleAccountId
      ? { accountId: effectiveSingleAccountId, includeSites: sitesSeuls || lesDeux }
      : "skip",
  );
  const rawCreators = isSuperadmin
    ? adminCreators
    : isAllScope
      ? myAccountsCreators
      : accountCreators;

  const statsBySlug = useCreatorStatsBySlug();
  const connBySlug = useCreatorConnectionsBySlug();
  return useMemo(() => {
    if (!rawCreators) return [];
    return (rawCreators as ConvexCreator[])
      // `includeSites` RAJOUTE les sites, il ne les isole pas : quand on veut
      // le parc des sites, on retire les créatrices ici.
      .filter((c) => lesDeux || (c.kind === "site") === sitesSeuls)
      .map((c) => projectToUiCreator(c, statsBySlug[c.slug], connBySlug[c.slug]));
  }, [rawCreators, statsBySlug, connBySlug, sitesSeuls, lesDeux]);
}
